"use client";

import React, { createContext, useContext, useEffect, useRef } from "react";
import { useCart as useCartHook } from "./useCart";
import { useSession } from "next-auth/react";
import { useCartStore } from "../store/cartStore";
import { isAdminSession, isSupplierSession, isFinanceSession } from "../utils/pricingVisibility";

type CartContextType = ReturnType<typeof useCartHook>;

const CartContext = createContext<CartContextType | null>(null);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const cart = useCartHook();
  const { data: session, status } = useSession();
  const { exchangeRate } = useCartStore();
  const fetchedRef = useRef(false);

  useEffect(() => {
    // 1. Evitar ejecución mientras se valida la sesión
    if (status === "loading") return;

    if (status === "authenticated" && session?.user?.accessToken) {
      // 2. Si es admin, proveedor o finanzas, no hacer la petición
      const isAdmin = isAdminSession(session);
      const isSupplier = isSupplierSession(session);
      const isFinance = isFinanceSession(session);
      if (isAdmin || isSupplier || isFinance) return;

      // 3. Ejecutar solo si hay tasa de cambio y solo una vez al cargar
      if (exchangeRate !== null && !fetchedRef.current) {
        cart.fetchCart();
        fetchedRef.current = true;
      }
    } else if (status === "unauthenticated") {
      if (cart.cart) {
        cart.setCart(null); // Limpiar carrito al cerrar sesión
      }
      fetchedRef.current = false; // Resetear para permitir fetch en el próximo login
    }
  }, [status, session?.user, exchangeRate, cart]);

  return <CartContext.Provider value={cart}>{children}</CartContext.Provider>;
}

export function useCartContext() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCartContext must be used within a CartProvider");
  }
  return context;
}
